import React, { useEffect } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { Button, FormControlLabel, FormGroup, makeStyles, MenuItem, Theme, Typography } from '@material-ui/core';
import { lightGrey } from '../../../app/theme';
import InviteFormValues from '../../../models/account/InviteFormValues';
import Invite from '../../../models/invite/Invite';
import AppState from '../../../models/redux/state/app.state';
import { inviteUserAPIcall } from '../../../redux/actions/auth/auth.actions';
import { getGroupsAPIcall } from '../../../redux/actions/enums/enums.actions';
import CheckboxField from '../../micro/CheckboxField/CheckboxField';
import TextFieldWrapper from '../../micro/TextFieldWrapper/TextFieldWrapper';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    margin: '17px 0 30px',
    borderBottom: `1px solid ${theme.palette.secondary.main}`,
    color: theme.palette.secondary.main,
    fontSize: 32,
    fontWeight: 600,
  },
  form: {
    maxWidth: 1020,
    width: '100%',
  },
  disabledNotice: {
    maxWidth: 1020,
    marginBottom: 24,
    color: theme.palette.error.main,
    fontSize: 14,
    fontWeight: 600,
  },
  formTextFields: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))',
    gap: 20,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: 0,
    },
  },
  formColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputField: {
    color: lightGrey,
    textTransform: 'uppercase',
  },
  control: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 5,
  },
  checkboxSection: {
    paddingTop: 16,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 24,
    [theme.breakpoints.down('sm')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
    },
  },
  header: {
    fontSize: 20,
    fontWeight: 600,
  },
  button: {
    borderRadius: 21,
    height: 42,
    minWidth: 120,
    marginBottom: 8,
  },
  instructions: {
    maxWidth: 900,
    padding: '35px 0 65px',
  },
  paragraph: {
    fontSize: 14,
    paddingBottom: 16,
    lineHeight: 1.15,
  },
}));

const inviteCreationSchema = Yup.object({
  firstName: Yup.string().required('*Required field'),
  lastName: Yup.string().required('*Required field'),
  emailAddress: Yup.string().email('Invalid email address').required('*Required field'),
  groups: Yup.string().required('*Required field'),
  axuminAcquisitionUngated: Yup.boolean(),
  axuminInterpretationUngated: Yup.boolean(),
  poslumaAcquisitionUngated: Yup.boolean(),
  poslumaInterpretationUngated: Yup.boolean(),
});

const initialFormValues: InviteFormValues = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  groups: '',
  axuminAcquisitionUngated: false,
  axuminInterpretationUngated: false,
  poslumaAcquisitionUngated: false,
  poslumaInterpretationUngated: false,
};

const InviteForm = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const groups = useSelector((state: AppState) => state.enums.groups);

  useEffect(() => {
    if (!groups.length) {
      dispatch(getGroupsAPIcall());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inviteUser = (invite: InviteFormValues, resetForm: () => void) => {
    const body: Invite = { ...invite, groups: [invite.groups], username: invite.emailAddress };
    dispatch(inviteUserAPIcall(body, resetForm));
  };

  return (
    <section aria-labelledby="invite-form-title">
      <Typography id="invite-form-title" className={classes.title}>
        Invite Form
      </Typography>
      <Typography className={classes.disabledNotice} role="status">
        The Invite Form is currently disabled because the site is using open enrollment. To switch from open enrollment
        to invite-only registration, please contact BrickSimple.
      </Typography>
      <Formik
        initialValues={initialFormValues}
        validationSchema={inviteCreationSchema}
        onSubmit={(values, actions) => inviteUser(values, actions.resetForm)}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {(formProps) => (
          <form onSubmit={formProps.handleSubmit} className={classes.form}>
            <div className={classes.formTextFields}>
              <div className={classes.formColumn}>
                <div className={classes.control}>
                  <TextFieldWrapper
                    form={formProps}
                    inputLabelProps={{ className: classes.inputField }}
                    label="FIRST NAME*"
                    formKey="firstName"
                    variant="outlined"
                  />
                </div>
                <div className={classes.control}>
                  <TextFieldWrapper
                    form={formProps}
                    inputLabelProps={{ className: classes.inputField }}
                    label="LAST NAME*"
                    formKey="lastName"
                    variant="outlined"
                  />
                </div>
              </div>
              <div className={classes.formColumn}>
                <div className={classes.control}>
                  <TextFieldWrapper
                    form={formProps}
                    inputLabelProps={{ className: classes.inputField }}
                    label="EMAIL ADDRESS*"
                    formKey="emailAddress"
                    variant="outlined"
                  />
                </div>
                <div className={classes.control}>
                  <TextFieldWrapper
                    variant="outlined"
                    label="GROUP*"
                    inputLabelProps={{ className: classes.inputField }}
                    form={formProps}
                    formKey="groups"
                    select
                  >
                    {groups.map((group) => (
                      <MenuItem key={group.id} value={group.url}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </TextFieldWrapper>
                </div>
              </div>
            </div>
            <div className={classes.checkboxSection}>
              <div>
                <Typography className={classes.header}>User has completed:</Typography>
                <FormGroup>
                  <FormControlLabel
                    control={<CheckboxField name="axuminAcquisitionUngated" style={{ color: lightGrey }} />}
                    label="Axumin Image Acquisition Training"
                  />
                  <FormControlLabel
                    control={<CheckboxField name="axuminInterpretationUngated" style={{ color: lightGrey }} />}
                    label="Axumin Image Interpretation Training"
                  />
                  <FormControlLabel
                    control={<CheckboxField name="poslumaAcquisitionUngated" style={{ color: lightGrey }} />}
                    label="POSLUMA Image Acquisition Training"
                  />
                  <FormControlLabel
                    control={<CheckboxField name="poslumaInterpretationUngated" style={{ color: lightGrey }} />}
                    label="POSLUMA Image Interpretation Training"
                  />
                </FormGroup>
              </div>
              <Button className={classes.button} type="submit" variant="contained" color="secondary" disabled>
                Send Invite
              </Button>
            </div>
          </form>
        )}
      </Formik>
      <div className={classes.instructions}>
        <Typography className={classes.header}>To Invite a New User:</Typography>
        <Typography className={classes.paragraph}>
          Fill out the four required fields. If the new user has completed one of the trainings outside of the platform,
          mark the checkbox next to that training. If they have not completed any trainings, leave the checkboxes
          unmarked. Finally, click the “SEND INVITE” button. Users will have up to seven business days to complete their
          registration.
        </Typography>
        <Typography className={classes.paragraph}>
          A confirmation message will appear at the bottom of the screen. Once the message clears, you can proceed to
          invite an additional user.
        </Typography>
      </div>
    </section>
  );
};

export default InviteForm;
